/**
 * useSemanticDateState Hook
 *
 * Manages all state for the SemanticDatePicker component.
 * Keeps committed value separate from pending selection for clean confirm-before-close.
 */

import { useState, useCallback, useMemo } from 'react'
import type { Dayjs } from 'dayjs'
import type { SemanticDateState, SemanticDateActions } from '../types'

interface UseSemanticDateStateOptions {
  /** The committed (external) value */
  value: Dayjs | null

  /** Called when user applies their selection */
  onChange?: (value: Dayjs | null) => void

  /** Called when open state changes */
  onOpenChange?: (open: boolean) => void
}

interface UseSemanticDateStateReturn {
  state: SemanticDateState
  actions: SemanticDateActions
}

/**
 * Hook that manages semantic date picker state.
 *
 * Key concept: There's a difference between:
 * - `value` (committed, external) - What's actually saved
 * - `pendingValue` (internal) - What user is currently selecting
 * - `wantsSemanticNull` (internal) - Whether user clicked the semantic NULL button
 *
 * The Apply button commits pendingValue/wantsSemanticNull to value.
 * Cancel/close discards pending state.
 */
export function useSemanticDateState({
  value: _value,
  onChange,
  onOpenChange,
}: UseSemanticDateStateOptions): UseSemanticDateStateReturn {
  // Panel open/closed state
  const [isOpen, setIsOpen] = useState(false)

  // Pending selection (not yet committed)
  const [pendingValue, setPendingValue] = useState<Dayjs | null>(null)

  // Whether user explicitly wants semantic NULL
  const [wantsSemanticNull, setWantsSemanticNull] = useState(false)

  // Calculate whether user has made a selection that can be applied
  // Note: We enable Apply if user has made ANY selection, even if it matches current value
  // This is better UX - user clicked something and expects to be able to confirm it
  const hasChange = useMemo(() => {
    // If user clicked the semantic null button, they can apply it
    if (wantsSemanticNull) return true

    // If user selected a date from calendar or presets, they can apply it
    if (pendingValue !== null) return true

    // No selection made yet
    return false
  }, [wantsSemanticNull, pendingValue])

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  const resetPendingState = useCallback(() => {
    setPendingValue(null)
    setWantsSemanticNull(false)
  }, [])

  const open = useCallback(() => {
    resetPendingState()
    setIsOpen(true)
    onOpenChange?.(true)
  }, [resetPendingState, onOpenChange])

  const close = useCallback(() => {
    resetPendingState()
    setIsOpen(false)
    onOpenChange?.(false)
  }, [resetPendingState, onOpenChange])

  const toggle = useCallback(() => {
    if (isOpen) {
      close()
    } else {
      open()
    }
  }, [isOpen, open, close])

  const selectDate = useCallback((date: Dayjs) => {
    setPendingValue(date)
    setWantsSemanticNull(false)
  }, [])

  const selectSemanticNull = useCallback(() => {
    setWantsSemanticNull(true)
    setPendingValue(null)
  }, [])

  const apply = useCallback(() => {
    let newValue: Dayjs | null | undefined

    if (wantsSemanticNull) {
      newValue = null
      onChange?.(null)
    } else if (pendingValue !== null) {
      newValue = pendingValue
      onChange?.(pendingValue)
    }
    // If neither, nothing to apply

    resetPendingState()
    setIsOpen(false)
    onOpenChange?.(false)

    return newValue
  }, [wantsSemanticNull, pendingValue, onChange, resetPendingState, onOpenChange])

  const cancel = useCallback(() => {
    close()
  }, [close])

  const reset = useCallback(() => {
    resetPendingState()
    setIsOpen(false)
  }, [resetPendingState])

  // -------------------------------------------------------------------------
  // Return
  // -------------------------------------------------------------------------

  const state: SemanticDateState = {
    isOpen,
    pendingValue,
    wantsSemanticNull,
    hasChange,
  }

  const actions: SemanticDateActions = {
    open,
    close,
    toggle,
    selectDate,
    selectSemanticNull,
    apply,
    cancel,
    reset,
  }

  return { state, actions }
}
